<template>
  <MediaLibraryRenderless
    v-slot="{
      getFileInputListeners,
      getFileInputProps,
      getDropZoneProps,
      getDropZoneListeners,
      removeMedia,
      state,
      clearInvalidMedia,
      getImgProps,
      getCustomPropertyInputProps,
      setOrder,
    }"
    :initial-value="initialValue"
    :validation-errors="validationErrors"
    :validation-rules="{
      accept: fileTypes,
      maxSizeInKB: maxSizeInKB,
    }"
    vapor
    :name="name"
    :multiple="multiple"
    :before-upload="
      (file, options) =>
        (options.axiosInstance.defaults.headers.common['X-CSRF-TOKEN'] =
          csrfToken)
    "
    @has-uploads-in-progress-change="(e) => emit('upload-in-progress', !!e)"
    @changed="(media) => emit('changed', media, getErrorMessage)"
  >
    <slot
      :get-file-input-listeners="getFileInputListeners"
      :get-file-input-props="getFileInputProps"
      :get-drop-zone-props="getDropZoneProps"
      :get-drop-zone-listeners="getDropZoneListeners"
      :remove-media="removeMedia"
      :state="state"
      :clear-invalid-media="clearInvalidMedia"
      :get-img-props="getImgProps"
      :get-custom-property-input-props="getCustomPropertyInputProps"
      :set-order="setOrder"
    />
    <template v-if="displayErrorMessage">
      <div v-for="(invalidMedia, index) in state.invalidMedia" :key="index">
        <small
          v-for="(error, errorIndex) in invalidMedia.errors"
          :key="errorIndex"
          class="block text-micro text-error subpixel-antialiased"
          v-text="getErrorMessage(error, invalidMedia.attributes.name)"
        />
      </div>
    </template>
  </MediaLibraryRenderless>
</template>
<script setup>
import { MediaLibraryRenderless } from '@spatie/media-library-pro-vue3'
import { computed } from 'vue'

const { useI18n } = await import('vue-i18n')
const { t } = useI18n()

const props = defineProps({
  name: {
    type: String,
    required: false,
    default: () => 'media',
  },
  initialValue: {
    type: [Array, Object],
    required: true,
  },
  validationErrors: {
    type: [Array, Object],
    required: false,
    default: () => ({}),
  },
  multiple: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  image: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  maxSizeInKB: {
    type: Number,
    required: false,
    default: () => 5120,
  },
  accept: {
    type: Array,
    required: false,
    default: () => [],
  },
  displayErrorMessage: {
    type: Boolean,
    required: false,
    default: () => true,
  },
})

const csrfToken = document.head.querySelector(
  "meta[name='csrf-token']",
)?.content

const emit = defineEmits(['changed', 'upload-in-progress'])

const extensionsMapping = {
  'image/jpg': ['.jpg', '.jpeg'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/svg+xml': ['.svg'],
  'image/tiff': ['.tiff'],
  'audio/mpeg': ['.mp3'],
  'video/mp4': ['.mp4'],
  'text/csv': ['.csv'],
  'text/calendar': ['.ics'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  'application/pdf': ['.pdf'],
  'application/zip': ['.zip'],
  'application/x-7z-compressed': ['.7z'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': [
    '.pptx',
  ],
  'application/x-tar': ['.tar'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
}

const fileTypes = computed(() => [
  ...(props.image ? ['image/jpg', 'image/jpeg', 'image/png'] : []),
  ...props.accept,
])

const extensions = computed(() => {
  const selectedExtensions = fileTypes.value.flatMap(
    (type) => extensionsMapping[type],
  )

  return [...new Set(selectedExtensions)]
})

const getErrorMessage = (error, filename) => {
  if (error.includes('You must upload a file of type')) {
    return t('media.validation.file.type.error', {
      filename,
      extensions: extensions.value.join(', '),
    })
  }
  if (error.includes('File too large')) {
    return t('media.validation.file.size.error', {
      filename,
      size: props.maxSizeInKB + 'KB',
    })
  }

  console.error('Unknown error while uploading', { error, filename }) // eslint-disable-line no-console

  return t('media.validation.file.upload.error', { filename })
}
</script>
